import {HttpClient} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {ServerEndpoints} from '../../../server.endpoints';
import {
  CreateFormRequirementRequest,
  FormRequirementResource,
  UpdateFormRequirementRequest
} from '../shared/models/form-requirement.model';

/**
 * Talks to the Forms service requirement API (`/api/forms/v2025.1/requirement`) through the gateway — the
 * manager-facing "hook" that marks a form as required for employees / employment-relationships. Manager-gated
 * server-side; a 403 surfaces if the caller isn't PlatformOwner/CompanyOwner/ShardAdmin.
 */
@Injectable({providedIn: 'root'})
export class FormRequirementHttpService {
  constructor(private http: HttpClient, @Inject('BASE_URL') private baseUrl: string) {
  }

  private get base(): string {
    return this.baseUrl + ServerEndpoints.Forms_Requirement__v2025_1;
  }

  /** List the company's requirements, optionally filtered to one form. */
  list(formKey?: string): Observable<FormRequirementResource[]> {
    const url = formKey ? `${this.base}?formKey=${formKey}` : this.base;
    return this.http.get<FormRequirementResource[]>(url);
  }

  /**
   * Hook a form as required for an employee or an employment-relationship.
   *
   * The request always carries **both** cohort policies and the window length. That is not belt-and-braces: the
   * server only applies its retired single-policy fallback when neither cohort field is present, and that
   * fallback grants new hires no window at all — so an omission would silently create a different assignment
   * from the one the manager configured.
   */
  create(request: CreateFormRequirementRequest): Observable<FormRequirementResource> {
    return this.http.post<FormRequirementResource>(this.base, request);
  }

  /**
   * Change an existing assignment's gating settings. The form key and the target are not editable server-side
   * (re-pointing either would move the effective-from date every deadline hangs off), so this only ever carries
   * the policies, the window and the temporal validity.
   */
  update(id: string, request: UpdateFormRequirementRequest): Observable<FormRequirementResource> {
    return this.http.put<FormRequirementResource>(`${this.base}/${id}`, request);
  }

  /** Remove a requirement. */
  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/${id}`);
  }
}
